import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';

const Header = ({ onConsultationClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const phoneNumbers = [
    { number: '+27 76 899 9014', tel: '+27768999014' },
    { number: '+27 66 248 4734', tel: '+27662484734' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-black">
              Yu<span className="text-[#FFD000]">Zu</span>
            </h1>
            <p className="text-xs text-gray-600 -mt-1">Recruitment Services</p>
            <p className="text-xs text-gray-500 italic">Zest for the right Talent</p>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#FFD000] transition-colors duration-200 font-medium">
              Services
            </button>
            <button onClick={() => scrollToSection('method')} className="text-gray-700 hover:text-[#FFD000] transition-colors duration-200 font-medium">
              Our Method
            </button>
            <Link to="/vacancies" className="text-gray-700 hover:text-[#FFD000] transition-colors duration-200 font-medium">
              Vacancies
            </Link>
            <button onClick={() => scrollToSection('insights')} className="text-gray-700 hover:text-[#FFD000] transition-colors duration-200 font-medium">
              Insights
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#FFD000] transition-colors duration-200 font-medium">
              About
            </button>
            <a 
              href={`tel:${phoneNumbers[0].tel}`}
              className="flex items-center text-gray-700 hover:text-[#FFD000] transition-colors duration-200 font-medium"
            >
              <Phone className="h-4 w-4 mr-1" />
              {phoneNumbers[0].number}
            </a>
            <Button 
              onClick={onConsultationClick}
              className="bg-[#FFD000] hover:bg-[#FFD000]/90 text-black font-semibold transition-all duration-300 hover:scale-105"
            >
              Get Started
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-black p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="lg:hidden fixed inset-0 bg-black/20 z-40" 
              onClick={() => setIsMobileMenuOpen(false)}
              style={{top: '80px'}}
            />
            {/* Menu */}
            <div className="lg:hidden fixed left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl" style={{top: '80px'}}>
              <nav className="flex flex-col py-4 px-6">
                <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#FFD000] hover:bg-gray-50 transition-colors duration-200 font-medium text-left py-3 px-2 rounded-lg">
                  Services
                </button>
                <button onClick={() => scrollToSection('method')} className="text-gray-700 hover:text-[#FFD000] hover:bg-gray-50 transition-colors duration-200 font-medium text-left py-3 px-2 rounded-lg">
                  Our Method
                </button>
                <Link 
                  to="/vacancies" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-[#FFD000] hover:bg-gray-50 transition-colors duration-200 font-medium text-left py-3 px-2 rounded-lg"
                >
                  Vacancies
                </Link>
                <button onClick={() => scrollToSection('insights')} className="text-gray-700 hover:text-[#FFD000] hover:bg-gray-50 transition-colors duration-200 font-medium text-left py-3 px-2 rounded-lg">
                  Insights
                </button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#FFD000] hover:bg-gray-50 transition-colors duration-200 font-medium text-left py-3 px-2 rounded-lg">
                  About
                </button>
                {/* Both phone numbers in mobile menu */}
                {phoneNumbers.map((phone, index) => (
                  <a 
                    key={index}
                    href={`tel:${phone.tel}`}
                    className="flex items-center text-gray-700 hover:text-[#FFD000] hover:bg-gray-50 transition-colors duration-200 font-medium py-3 px-2 rounded-lg"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    {phone.number}
                  </a>
                ))}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <Button 
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      onConsultationClick();
                    }}
                    className="bg-[#FFD000] hover:bg-[#FFD000]/90 text-black font-semibold w-full py-3"
                  >
                    Get Started
                  </Button>
                </div>
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
